import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Info, Loader2, X } from 'lucide-react';
import { type ReactNode, useSyncExternalStore } from 'react';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'ink';

const TONES: Record<Tone, string> = {
  neutral: 'bg-tile text-ink-2',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  accent: 'bg-accent-soft text-accent-text',
  ink: 'bg-ink text-on-ink',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex h-6 items-center gap-1 rounded-sm px-2 text-[12px] font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ label = 'Загрузка' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-10 text-ink-2" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span className="text-[13px]">{label}…</span>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed border-line-strong bg-surface px-6 py-8">
      <h3 className="text-[16px] leading-6 font-semibold text-ink">{title}</h3>
      {children ? <div className="max-w-xl text-[14px] text-ink-2">{children}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** Ссылка «Расширить тариф» из ответа API (409 limit_exceeded, 403 license_readonly — details.expand) */
function expandPath(error: unknown): string | null {
  const d = (error as { details?: unknown } | null)?.details as { expand?: unknown } | undefined;
  return typeof d?.expand === 'string' && d.expand.startsWith('/') ? d.expand : null;
}

export function ErrorBox({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : 'Что-то пошло не так. Повторите действие.';
  const expand = expandPath(error);
  return (
    <div
      role="alert"
      className={clsx(
        'flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2.5 text-[13px] text-danger',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>
        {message}
        {expand ? (
          <>
            {' '}
            <a href={expand} className="font-medium underline underline-offset-2">
              Открыть лицензию
            </a>
          </>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Ошибки полей, которым на форме нет места (например, вложенные лимиты): показать общим сообщением, иначе кнопка
 * «молча не работает». known — имена полей, у которых есть своя подпись ошибки.
 */
export function unboundErrors(errors: Record<string, string>, known: readonly string[]): Error | null {
  const rest = Object.entries(errors).filter(([k]) => !known.includes(k));
  if (!rest.length) return null;
  return new Error(rest.map(([k, v]) => `${k}: ${v}`).join('; '));
}

/* ───────────── Уведомления ───────────── */

interface ToastItem {
  id: number;
  tone: 'success' | 'danger' | 'info';
  text: string;
}

let toasts: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function toast(text: string, tone: ToastItem['tone'] = 'success') {
  const id = ++seq;
  toasts = [...toasts, { id, tone, text }];
  emit();
  setTimeout(() => dismiss(id), tone === 'danger' ? 7000 : 4000);
}

function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function Toaster() {
  const items = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => toasts,
  );
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4"
      aria-live="polite"
    >
      {items.map((t) => {
        const Icon = t.tone === 'success' ? CheckCircle2 : t.tone === 'danger' ? AlertTriangle : Info;
        return (
          <div
            key={t.id}
            className="pointer-events-auto flex w-full max-w-md items-center gap-2.5 rounded-md bg-ink py-3 pr-2 pl-4 text-[14px] text-on-ink shadow-md md:w-auto md:pr-3"
          >
            <Icon
              className={clsx(
                'mt-0.5 size-4 shrink-0',
                t.tone === 'danger' ? 'text-[#F1767D]' : 'text-accent',
              )}
              aria-hidden
            />
            <span className="flex-1">{t.text}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Закрыть"
              className="-my-2 -mr-2 inline-flex size-11 shrink-0 items-center justify-center text-on-ink/60 hover:text-on-ink md:-my-1 md:-mr-1 md:size-6"
            >
              <X className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
